import React from "react";
import { Helmet } from "react-helmet";

export default function DigitalMarketingPage() {

  const services = [
    {
      title: "SEO Services",
      desc: "Technical SEO, local SEO, semantic SEO, topical authority systems, and organic traffic growth.",
      icon: "🚀",
    },
    {
      title: "Google Ads",
      desc: "High-converting Google Ads campaigns optimized for ROAS, conversions, and lead generation.",
      icon: "📈",
    },
    {
      title: "Social Media Marketing",
      desc: "Instagram, Facebook, LinkedIn, and YouTube growth campaigns for visibility and engagement.",
      icon: "🔥",
    },
    {
      title: "Content Marketing",
      desc: "SEO blogs, landing pages, authority content, and conversion-focused copywriting.",
      icon: "✍️",
    },
    {
      title: "Performance Marketing",
      desc: "Data-driven campaigns focused on measurable ROI, revenue growth, and scaling.",
      icon: "⚡",
    },
    {
      title: "Local SEO",
      desc: "Google Business Profile optimization, Maps ranking, reputation management, and local visibility.",
      icon: "📍",
    },
  ];

  const industries = [
    "Digital Marketing for Startups",
    "Digital Marketing for SaaS Companies",
    "Digital Marketing for Healthcare",
    "Digital Marketing for Schools & Education",
    "Digital Marketing for Manufacturers",
    "Digital Marketing for Real Estate",
    "Digital Marketing for eCommerce Brands",
    "Digital Marketing for Local Businesses",
  ];

  const faqs = [
    {
      q: "Why is digital marketing important?",
      a: "Digital marketing helps businesses increase online visibility, generate leads, improve conversions, and scale revenue faster.",
    },
    {
      q: "How long does SEO take?",
      a: "SEO generally takes 3–6 months for measurable growth depending on competition, authority, and content quality.",
    },
    {
      q: "Which is better SEO or Google Ads?",
      a: "SEO provides long-term organic growth while Google Ads delivers immediate traffic and faster lead generation.",
    },
    {
      q: "Do you provide local SEO services?",
      a: "Yes. We provide Google Business optimization, local citations, maps ranking, and reputation management services.",
    },
  ];

  return (
    <div className="bg-[#050816] text-white overflow-hidden">

      
  <Helmet>

    {/* PRIMARY SEO */}
    <title>
      Digital Marketing Agency | SEO, Google Ads & Performance Marketing | SoftCoders
    </title>

    <meta
      name="description"
      content="SoftCoders is a digital marketing agency providing SEO services, Google Ads management, social media marketing, local SEO, content marketing, performance marketing, and lead generation solutions for startups, SaaS companies, healthcare brands, eCommerce businesses, and enterprises."
    />

    <meta
      name="keywords"
      content="
      digital marketing agency,
      digital marketing company,
      seo services company,
      google ads agency,
      performance marketing agency,
      social media marketing company,
      local seo services,
      content marketing agency,
      lead generation services,
      seo agency india,
      search engine optimization,
      google ads management,
      meta ads agency,
      facebook ads company,
      instagram marketing services,
      youtube marketing agency,
      ecommerce marketing agency,
      saas marketing agency,
      healthcare digital marketing,
      startup marketing agency,
      digital advertising company,
      online marketing company,
      conversion optimization,
      organic traffic growth,
      technical seo services,
      semantic seo agency,
      enterprise seo company,
      local business seo,
      ppc advertising agency,
      digital growth agency
      "
    />

    <meta name="author" content="SoftCoders" />
    <meta name="robots" content="index, follow" />
    <meta name="language" content="English" />
    <meta name="revisit-after" content="2 days" />

    {/* CANONICAL */}
    <link
      rel="canonical"
      href="https://softcoders.in/digital-marketing"
    />

    {/* OPEN GRAPH */}
    <meta
      property="og:title"
      content="Digital Marketing Agency | SoftCoders"
    />

    <meta
      property="og:description"
      content="SEO services, Google Ads management, social media marketing, local SEO, and performance marketing solutions for business growth."
    />

    <meta
      property="og:url"
      content="https://softcoders.in/digital-marketing"
    />

    <meta property="og:type" content="website" />

    <meta
      property="og:image"
      content="https://softcoders.in/og-digital-marketing.jpg"
    />

    {/* TWITTER SEO */}
    <meta name="twitter:card" content="summary_large_image" />

    <meta
      name="twitter:title"
      content="Digital Marketing Agency | SoftCoders"
    />

    <meta
      name="twitter:description"
      content="Professional SEO, Google Ads, social media marketing, and performance marketing services for scalable business growth."
    />

    <meta
      name="twitter:image"
      content="https://softcoders.in/og-digital-marketing.jpg"
    />

    {/* GEO SEO */}
    <meta name="geo.region" content="IN" />
    <meta name="geo.country" content="India" />

    {/* MOBILE SEO */}
    <meta
      name="viewport"
      content="width=device-width, initial-scale=1"
    />

    <meta
      name="theme-color"
      content="#050816"
    />

    {/* SCHEMA */}
    <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "ProfessionalService",
        name: "SoftCoders",
        url: "https://softcoders.in",
        logo: "https://softcoders.in/logo.png",
        image: "https://softcoders.in/og-digital-marketing.jpg",
        description:
          "SoftCoders provides SEO services, Google Ads management, social media marketing, local SEO, and performance marketing solutions.",
        address: {
          "@type": "PostalAddress",
          addressCountry: "India"
        },
        areaServed: {
          "@type": "Country",
          name: "India"
        },
        serviceType: [
          "SEO Services",
          "Google Ads Management",
          "Performance Marketing",
          "Social Media Marketing",
          "Content Marketing",
          "Local SEO"
        ],
        sameAs: [
          "https://www.facebook.com/",
          "https://www.instagram.com/",
          "https://www.linkedin.com/"
        ]
      })}
    </script>

    {/* FAQ SCHEMA */}
    <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "Why is digital marketing important?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Digital marketing helps businesses increase online visibility, generate leads, improve conversions, and scale revenue faster."
            }
          },
          {
            "@type": "Question",
            name: "How long does SEO take?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "SEO generally takes 3 to 6 months depending on competition, authority, and content quality."
            }
          },
          {
            "@type": "Question",
            name: "Which is better SEO or Google Ads?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "SEO provides long-term organic growth while Google Ads delivers immediate traffic and faster lead generation."
            }
          },
          {
            "@type": "Question",
            name: "Do you provide local SEO services?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes. We provide Google Business optimization, local citations, maps ranking, and reputation management services."
            }
          }
        ]
      })}
    </script>

  </Helmet>

      {/* =========================
          GLOBAL STYLES + ANIMATIONS
      ========================== */}
      <style>{`

        html {
          scroll-behavior: smooth;
        }

        @keyframes fadeUp {
          from {
            opacity: 0;
            transform: translateY(60px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeLeft {
          from {
            opacity: 0;
            transform: translateX(-60px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes fadeRight {
          from {
            opacity: 0;
            transform: translateX(60px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes float {
          0%,100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-18px);
          }
        }

        @keyframes rotate {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes glowPulse {
          0%,100% {
            opacity: .5;
            transform: scale(1);
          }
          50% {
            opacity: 1;
            transform: scale(1.1);
          }
        }

        @keyframes wave {
          0% {
            transform: translateX(0px);
          }
          50% {
            transform: translateX(15px);
          }
          100% {
            transform: translateX(0px);
          }
        }

        .fade-up {
          animation: fadeUp 1s ease forwards;
        }

        .fade-left {
          animation: fadeLeft 1s ease forwards;
        }

        .fade-right {
          animation: fadeRight 1s ease forwards;
        }

        .float {
          animation: float 7s ease-in-out infinite;
        }

        .rotate {
          animation: rotate 25s linear infinite;
        }

        .glow-pulse {
          animation: glowPulse 5s ease infinite;
        }

        .wave {
          animation: wave 3s ease infinite;
        }

      `}</style>

      {/* =========================
            HERO SECTION
      ========================== */}
      <section className="relative min-h-screen flex items-center overflow-hidden border-b border-white/10">

        {/* BACKGROUND */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#050816] via-[#0B1023] to-[#050816]" />

        {/* GRID */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(99,102,241,.4) 1px, transparent 1px),
              linear-gradient(90deg, rgba(99,102,241,.4) 1px, transparent 1px)
            `,
            backgroundSize: "80px 80px",
          }}
        />

        {/* GLOW */}
        <div className="absolute top-[-120px] left-[-120px] w-[350px] sm:w-[500px] h-[350px] sm:h-[500px] bg-indigo-600/20 rounded-full blur-3xl glow-pulse"></div>

        <div className="absolute bottom-[-100px] right-[-100px] w-[350px] sm:w-[500px] h-[350px] sm:h-[500px] bg-purple-600/20 rounded-full blur-3xl glow-pulse"></div>

        {/* ROTATING CIRCLES */}
        <div className="absolute right-[10%] top-[10%] w-[250px] sm:w-[420px] h-[250px] sm:h-[420px] border border-indigo-500/10 rounded-full rotate"></div>

        <div className="absolute right-[15%] top-[15%] w-[180px] sm:w-[320px] h-[180px] sm:h-[320px] border border-cyan-500/10 rounded-full rotate"></div>

        {/* FLOATING ELEMENTS */}
        <div className="absolute top-32 left-[8%] hidden lg:block float">
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl px-5 py-4">
            <div className="text-sm text-slate-400">
              Organic Growth
            </div>
            <div className="text-3xl font-black text-indigo-400 mt-1">
              +500%
            </div>
          </div>
        </div>

        <div className="absolute bottom-32 left-[12%] hidden lg:block float">
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl px-5 py-4">
            <div className="text-sm text-slate-400">
              Google Rankings
            </div>
            <div className="text-3xl font-black text-cyan-400 mt-1">
              #1 Keywords
            </div>
          </div>
        </div>

        {/* MAIN */}
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 py-24 w-full">

          <div className="grid lg:grid-cols-2 gap-20 items-center">

            {/* LEFT */}
            <div className="fade-left">

              <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full border border-indigo-500/20 bg-indigo-500/10 backdrop-blur-xl">

                <div className="w-2 h-2 rounded-full bg-indigo-400 animate-pulse"></div>

                <span className="text-indigo-300 text-sm font-medium tracking-wide">
                  #1 Digital Marketing Agency
                </span>

              </div>

              <h1 className="mt-8 text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-black leading-[0.95] tracking-tight">

                Grow Your
                <span className="block bg-gradient-to-r from-indigo-400 via-cyan-400 to-purple-400 text-transparent bg-clip-text">
                  Traffic,
                </span>

                Leads & Revenue
                <span className="block">
                  Faster 🚀
                </span>

              </h1>

              <p className="mt-8 text-base sm:text-lg lg:text-xl leading-8 text-slate-400 max-w-2xl">

                SoftCoders is a full-service digital marketing agency
                helping startups, SaaS companies, local businesses,
                healthcare brands, and enterprises grow through
                SEO services, Google Ads, content marketing,
                local SEO, social media marketing,
                and performance marketing strategies.

              </p>

              {/* BUTTONS */}
              <div className="flex flex-wrap gap-5 mt-10">

                <a
    href="https://wa.me/919254147455?text=Hello%20SoftCoders%2C%20I%20want%20to%20discuss%20my%20project."
    target="_blank"
    rel="noopener noreferrer"
 className="group relative overflow-hidden bg-indigo-600 hover:bg-indigo-500 px-8 py-4 rounded-2xl font-bold transition-all duration-300 hover:-translate-y-1 shadow-[0_20px_60px_rgba(99,102,241,0.35)]">

                  <span className="relative z-10">
                    Get Free SEO Audit
                  </span>

                  <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-purple-500 opacity-0 group-hover:opacity-100 transition duration-300"></div>

                </a>

                <a
    href="/contact" className="border border-white/10 hover:border-indigo-500/30 hover:bg-indigo-500/10 transition-all duration-300 px-8 py-4 rounded-2xl text-slate-300 hover:-translate-y-1">

                  View Case Studies

                </a>

              </div>

              {/* STATS */}
              <div className="grid grid-cols-3 gap-5 sm:gap-10 mt-14">

                {[
                  ["500%", "Traffic Growth"],
                  ["12M+", "Ad Reach"],
                  ["4.8x", "Lead Increase"],
                ].map(([num, label], i) => (

                  <div key={i} className="fade-up">

                    <div className="text-3xl sm:text-4xl lg:text-5xl font-black text-indigo-400">
                      {num}
                    </div>

                    <div className="text-slate-500 text-sm mt-2">
                      {label}
                    </div>

                  </div>

                ))}

              </div>

            </div>

            {/* RIGHT */}
            <div className="relative fade-right">

              {/* MAIN CARD */}
              <div className="relative bg-white/[0.04] border border-white/10 backdrop-blur-2xl rounded-[35px] p-5 sm:p-8 shadow-[0_30px_80px_rgba(0,0,0,0.5)] overflow-hidden">

                {/* TOP */}
                <div className="flex items-start justify-between mb-10">

                  <div>

                    <div className="text-sm text-slate-500">
                      Campaign Performance
                    </div>

                    <div className="text-5xl sm:text-6xl font-black mt-3">
                      +428%
                    </div>

                  </div>

                  <div className="px-4 py-2 rounded-xl bg-green-500/10 border border-green-500/20 text-green-400 text-sm font-bold wave">
                    Scaling ↗
                  </div>

                </div>

                {/* KEYWORDS */}
                <div className="space-y-4">

                  {[
                    ["digital marketing agency", "#1"],
                    ["seo company india", "#2"],
                    ["google ads agency", "#1"],
                  ].map(([keyword, rank], i) => (

                    <div
                      key={i}
                      className="flex items-center justify-between bg-white/[0.03] border border-white/5 rounded-2xl p-5 hover:border-indigo-500/20 transition"
                    >

                      <div>

                        <div className="font-semibold text-sm sm:text-base">
                          {keyword}
                        </div>

                        <div className="text-xs sm:text-sm text-slate-500 mt-1">
                          High Intent Keyword
                        </div>

                      </div>

                      <div className="text-3xl font-black text-indigo-400">
                        {rank}
                      </div>

                    </div>

                  ))}

                </div>

                {/* GRAPH */}
                <div className="mt-10 bg-gradient-to-br from-[#11182D] to-[#0B1022] rounded-[30px] p-6 border border-white/5">

                  <div className="flex items-center justify-between mb-10">

                    <div>

                      <div className="text-sm text-slate-500">
                        Monthly Growth
                      </div>

                      <div className="text-4xl font-black mt-2">
                        +500%
                      </div>

                    </div>

                    <div className="text-green-400 font-bold">
                      Revenue ↑
                    </div>

                  </div>

                  {/* BARS */}
                  <div className="h-[240px] flex items-end gap-3">

                    {[40, 70, 100, 140, 170, 220, 260].map((h, i) => (

                      <div
                        key={i}
                        className="relative flex-1 flex items-end group"
                      >

                        <div
                          className="absolute bottom-0 w-full rounded-t-[24px] blur-xl bg-indigo-500/30"
                          style={{
                            height: `${h}px`,
                          }}
                        />

                        <div
                          className="relative w-full rounded-t-[24px] bg-gradient-to-t from-indigo-700 via-indigo-500 to-cyan-300 transition-all duration-500 group-hover:scale-105"
                          style={{
                            height: `${h}px`,
                          }}
                        />

                      </div>

                    ))}

                  </div>

                </div>

              </div>

            </div>

          </div>

        </div>

      </section>

      {/* =========================
            SERVICES
      ========================== */}
      <section className="py-24 border-b border-white/5">

        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">

          <div className="text-center max-w-3xl mx-auto">

            <div className="text-indigo-400 uppercase tracking-[0.2em] text-sm font-semibold">
              DIGITAL MARKETING SERVICES
            </div>

            <h2 className="text-4xl sm:text-5xl font-black mt-6 leading-tight">

              Full-Service Digital Marketing
              <span className="block text-indigo-400">
                Solutions For Business Growth
              </span>

            </h2>

            <p className="text-slate-400 leading-8 mt-8">

              We provide SEO services, Google Ads management,
              social media marketing, local SEO,
              performance marketing, and content marketing strategies
              designed to increase traffic, leads, and revenue.

            </p>

          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-20">

            {services.map((service, i) => (

              <div
                key={i}
                className="group relative overflow-hidden bg-[#0E142B] border border-white/10 rounded-3xl p-8 hover:border-indigo-500/30 hover:-translate-y-3 transition-all duration-500"
              >

                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-br from-indigo-500/10 to-purple-500/10 transition duration-500"></div>

                <div className="relative z-10">

                  <div className="text-5xl mb-6">
                    {service.icon}
                  </div>

                  <h3 className="text-2xl font-bold mb-4 group-hover:text-indigo-300 transition">
                    {service.title}
                  </h3>

                  <p className="text-slate-400 leading-8">
                    {service.desc}
                  </p>

                </div>

              </div>

            ))}

          </div>

        </div>

      </section>

      {/* =========================
            INDUSTRIES
      ========================== */}
      <section className="py-24 border-b border-white/5">

        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">

          <div className="max-w-3xl">

            <div className="text-indigo-400 uppercase tracking-[0.2em] text-sm font-semibold">
              INDUSTRIES WE SERVE
            </div>

            <h2 className="text-4xl sm:text-5xl font-black mt-6 leading-tight">

              Digital Marketing Services
              <span className="block text-indigo-400">
                For High-Growth Industries
              </span>

            </h2>

          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">

            {industries.map((item, i) => (

              <div
                key={i}
                className="bg-[#0E142B] border border-white/10 rounded-2xl p-6 hover:border-indigo-500/30 hover:-translate-y-2 transition-all duration-300"
              >

                <h3 className="font-bold text-lg leading-8">
                  {item}
                </h3>

              </div>

            ))}

          </div>

        </div>

      </section>

      {/* =========================
            SEO CONTENT
      ========================== */}
      <section className="py-24 border-b border-white/5">

        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-10">

          <h2 className="text-4xl sm:text-5xl font-black leading-tight">

            Best Digital Marketing Agency in India

          </h2>

          <div className="space-y-8 text-slate-400 text-lg leading-9 mt-12">

            <p>

              SoftCoders is a leading digital marketing agency in India
              providing SEO services, Google Ads management,
              local SEO, content marketing,
              performance marketing,
              and social media marketing solutions
              for startups, SaaS businesses,
              healthcare brands, educational institutions,
              manufacturers, eCommerce companies,
              and local businesses.

            </p>

            <p>

              Our SEO experts help businesses improve search rankings,
              increase organic traffic, generate qualified leads,
              and build topical authority through semantic SEO,
              technical SEO optimization,
              content strategy,
              internal linking systems,
              and conversion-focused landing pages.

            </p>

            <p>

              We create high-performance Google Ads campaigns,
              retargeting funnels,
              YouTube advertising campaigns,
              and Meta Ads strategies
              optimized for lead generation,
              conversion growth,
              and maximum return on ad spend.

            </p>

          </div>

        </div>

      </section>

      {/* =========================
            FAQ
      ========================== */}
      <section className="py-24">

        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-10">

          <div className="text-center mb-20">

            <div className="text-indigo-400 uppercase tracking-[0.2em] text-sm font-semibold">
              FAQ
            </div>

            <h2 className="text-4xl sm:text-5xl font-black mt-6">

              Digital Marketing Questions

            </h2>

          </div>

          <div className="space-y-6">

            {faqs.map((faq, i) => (

              <div
                key={i}
                className="bg-[#0E142B] border border-white/10 rounded-3xl p-8 hover:border-indigo-500/20 transition"
              >

                <h3 className="text-2xl font-bold">
                  {faq.q}
                </h3>

                <p className="text-slate-400 leading-8 mt-4">
                  {faq.a}
                </p>

              </div>

            ))}

          </div>

        </div>

      </section>

      {/* CTA SECTION */}

    </div>
  );
}